import {
  Container,
  Form,
  Icons,
  Option,
  Select,
  Tooltip,
} from "@ster/ster-toolkit";
import { useForm } from "antd/lib/form/Form";
import { Store } from "antd/lib/form/interface";
import { SelectValue } from "antd/lib/select";
import { memo, useCallback, useEffect, useMemo } from "react";

import { endOfMonth, startOfMonth } from "date-fns";
import { PackageExtended } from "../../api";
import { Medium, PackageData, SubOrder } from "../../types";
import { getPackageTooltip } from "../../utils";
import PackageSelect from "../PackageSelect";
import PeriodInput from "../PeriodInput";
import BudgetGrpsInput from "./BudgetGrpsInput";
import styles from "./SubOrderForm.module.less";
import TargetGroupSelect from "./TargetGroupSelect";
import Top2000Warning from "./Top2000Warning";

const SubOrderForm = ({
  subOrder,
  medium,
  period,
  onDeleteClick,
  onUpdateSubOrder,
  packages,
  spotLengths,
}: {
  subOrder: SubOrder;
  medium: Medium;
  period: [Date, Date];
  onDeleteClick: (id: number) => void;
  onUpdateSubOrder: (newSubOrder: SubOrder) => void;
  packages: PackageExtended[];
  spotLengths: number[];
}): JSX.Element | null => {
  const [form] = useForm<SubOrder>();

  const selectedPackage = useMemo(
    () =>
      packages.find(({ targetGroups }) =>
        targetGroups.some(
          ({ packageCode }) => packageCode === subOrder.packageCode,
        ),
      ),
    [packages, subOrder.packageCode],
  );

  const selectedTargetGroup = useMemo(
    () =>
      selectedPackage?.targetGroups.find(
        ({ index, packageCode }) =>
          index === subOrder.targetGroupIndex &&
          packageCode === subOrder.packageCode,
      ),
    [
      selectedPackage?.targetGroups,
      subOrder.packageCode,
      subOrder.targetGroupIndex,
    ],
  );

  useEffect(() => {
    if (!selectedPackage) {
      return;
    }

    form.setFieldsValue({
      ...subOrder,
      packageName: selectedPackage.name,
      packageCode: selectedTargetGroup?.packageCode,
      channels: selectedTargetGroup?.channels,
      targetGroupIndex: selectedTargetGroup?.index,
    });
    form.validateFields(["period"]);
  }, [
    form,
    selectedPackage,
    selectedTargetGroup?.channels,
    selectedTargetGroup?.index,
    selectedTargetGroup?.packageCode,
    subOrder,
  ]);

  const handleFormChange = useCallback(
    (changedValues: Store) => {
      onUpdateSubOrder({ ...subOrder, ...changedValues });
    },
    [onUpdateSubOrder, subOrder],
  );

  const handleTargetGroupChange = useCallback(
    (value: SelectValue) => {
      const targetGroup = selectedPackage?.targetGroups.find(
        ({ index }) => index === value,
      );

      onUpdateSubOrder({
        ...subOrder,
        targetGroupId: targetGroup?.targetGroupId,
        targetGroupIndex: value as number,
        packageCode: targetGroup?.packageCode,
        channels: targetGroup?.channels,
      });
    },
    [onUpdateSubOrder, selectedPackage?.targetGroups, subOrder],
  );

  const handlePackageChange = useCallback(
    ({
      packageName,
      packageCode,
      targetGroupIndex,
      targetGroupId,
      channels,
    }: PackageData) => {
      onUpdateSubOrder({
        ...subOrder,
        packageName,
        packageCode,
        targetGroupIndex,
        targetGroupId,
        channels,
      });
    },
    [onUpdateSubOrder, subOrder],
  );

  if (!selectedPackage) {
    return null;
  }

  return (
    <Container key={subOrder.id} className={styles.container}>
      <Tooltip title="Verwijder deze inkoopoptie">
        <button
          className={styles.delete}
          type="button"
          onClick={(): void => onDeleteClick(subOrder.id)}
          aria-label="Verwijder"
        >
          <Icons.DeleteIcon />
        </button>
      </Tooltip>
      <Form
        form={form}
        onValuesChange={handleFormChange}
        layout="horizontal"
        className={styles.suborders}
      >
        <Form.Item label="Periode" shouldUpdate>
          {({ getFieldValue }) => {
            const subOrderPeriod = getFieldValue("period");
            return (
              <PeriodInput
                medium={medium}
                period={subOrderPeriod ?? period}
                inBetween={[startOfMonth(period[0]), endOfMonth(period[1])]}
                packageCode={subOrder.packageCode}
                allowClear={false}
              />
            );
          }}
        </Form.Item>
        <Form.Item
          label="Spotlengte"
          name="spotLength"
          helpText="Een tag-on is onderdeel van de ingevulde spotlengte. Heb je een hoofdspot van 15 seconden en een tag-on van 5 seconden? Kies dan '20' bij spotlengte."
        >
          <Select className={styles.spotLengthSelect}>
            {spotLengths.map((sl) => (
              <Option key={sl} value={sl}>
                {sl}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          required
          label="Inkoopoptie"
          helpText={
            <div className={styles.packageHelp}>
              De getoonde inkoopopties zijn inclusief spreiding.
              <table>
                <tbody>
                  {packages.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{getPackageTooltip(p.name)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        >
          <PackageSelect
            medium={medium}
            period={period}
            onChange={handlePackageChange}
            value={subOrder}
          />
        </Form.Item>
        <TargetGroupSelect
          handleTargetGroupChange={handleTargetGroupChange}
          selectedPackage={selectedPackage}
          medium={medium}
        />
        <BudgetGrpsInput medium={medium} subOrder={subOrder} />
      </Form>
      <Top2000Warning
        from={period[0]}
        to={period[1]}
        medium={medium}
        channels={selectedTargetGroup?.channels}
      />
    </Container>
  );
};

export default memo(SubOrderForm);
